<template>
  <div
    :class="[
      'date-field-group',
      { error: error, calendar: calendarIcon, disabled: disabled }
    ]"
    @click="onFocused"
  >
    <div :class="['date-field-wrap', name]">
      <label
        :for="name"
        @click="onFocused"
      >
        {{ label }}
      </label>
      <input
        ref="input"
        type="text"
        :placeholder="placeholder"
        :value="value"
        :name="name"
        :disabled="disabled"
        maxlength="10"
        autocomplete="off"
        readonly="true"
        @click="onFocused"
        @focus="onFocused"
        @change="onChange($event.target.value)"
        @input="onInput($event.target.value)"
        @keydown="onKeyDown"
        @keyup.tab="onTabPressed"
        @keyup.tab.shift.exact="onShiftTabPressed"
      />
    </div>

    <div
      v-if="error && errorMsg"
      class="error-msg"
    >
      {{ errorMsg }}
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import isEmpty from 'lodash-es/isEmpty'

  /**
   * Exported events
   */
  const events = {
    FOCUSED: 'focused'
  }

  export default {
    model: {
      prop: 'value',
      event: 'change'
    },
    props: {
      placeholder: {
        type: String,
        required: true
      },
      error: {
        type: Boolean,
        required: true
      },
      errorMsg: {
        type: String,
        default: ''
      },
      label: {
        type: String,
        default: '',
        required: false
      },
      isReturn: {
        type: Boolean,
        default: false
      },
      name: {
        type: String,
        required: false,
        default: ''
      },
      value: {
        type: String,
        default: '',
        required: false
      },
      isMobile: {
        type: Boolean,
        default: false
      },
      calendarIcon: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      formattedValue() {
        if (!isEmpty(this.value)) {
          return moment(this.value).format('DD.MM.YYYY')
        }
        return ''
      },
      formattedInput: {
        get() {
          if (!isEmpty(this.value)) {
            return moment(this.value).format('DD.MM.YYYY')
          }
          return ''
        },
        set(newValue) {
          if (!isEmpty(newValue)) {
            this.value = moment(newValue).format('DD.MM.YYYY')
          }
          this.value = ''
        }
      }
    },
    methods: {
      onTabPressed() {
        this.$emit('tabPressed')
      },
      onShiftTabPressed() {
        this.$emit('shiftTabPressed')
      },
      onFocused() {
        if (!this.disabled) {
          this.$emit(events.FOCUSED)
        }
      },
      hasFocus() {
        return this.$refs.input === document.activeElement
      },
      focusInput() {
        this.$refs.input.focus()
      },
      blurInput() {
        this.$refs.input.blur()
      },
      onChange(value) {
        if (this.isReturn) {
          this.$emit('maxDatePicked', value)
        } else {
          this.$emit('minDatePicked', value)
        }
      },
      onInput(value) {
        if (value.length === 10) {
          if (this.isReturn) {
            this.$emit('maxDatePicked', value)
          } else {
            this.$emit('minDatePicked', value)
          }
        }
      },
      onKeyDown(event) {
        let code
        if (window.event || event.keyCode) {
          code = event.keyCode
        } else {
          code = event.which
        }
        // backspace, tab, delete, enter(46), decimal point, dot, arrows(37-40) + numbers
       const allowedCodes = [8, 9, 13, 46, 110, 190,
          37, 38, 39, 40,
          48, 49, 50, 51, 52, 53, 54, 55, 56, 57,
          96, 97, 98, 99, 100, 101, 102, 103, 104, 105
       ]

        if (event.shiftKey === true && code === 9) {
          return
        }

        if (allowedCodes.indexOf(code) < 0 || event.shiftKey === true) {
          event.preventDefault()
        }
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .date-field-group {
    margin-bottom: 8px;
    width: 100%;
    position: relative;

    &:after {
      @include icon-font-thin();
      position: absolute;
      transform: rotate(90deg);
      font-size: em(12);
      font-weight: 700;
      line-height: 1;
      top: 20px;
      right: 14px;
      content: $icon-arrow-right-thin-medium;
      color: $blue-medium;
    }

    &.calendar {
      &:after {
        content: url('~assets/images/calendar.svg');
        font-size: em(16);
        color: $blue-dark;
        transform: none;
      }
    }

    .date-field-wrap {
      display: flex;
      flex-direction: column;
      background: #ffffff;
      border-radius: 3px;
      border: 1px solid rgba(#000000, 0.1);
      padding: 6px 25px 5px 10px;
      width: 100%;

      label {
        @include text-overflow();
        width: 100%;
        font-size: em(11);
        line-height: 13px;
        color: $input-placeholder;

        @include bp-mf-tablet {
          font-size: em(11);
          line-height: 13px;
        }
      }

      input {
        width: 100%;
        height: 19px;
        padding: 0;
        display: block;
        font-size: em(14);
        line-height: 16px;
        color: $grey-default;
        margin-top: 3px;
        background: transparent;
        text-transform: uppercase;

        @include input-placeholder {
          color: $input-placeholder;
          text-transform: none;
        }
      }
    }

    &.error {
      .date-field-wrap {
        border: 1px solid $red-dark !important;
      }
    }

    &.disabled {
      .date-field-wrap {
        background-color: #e4e6ea;
        border: none !important;
      }

      &.calendar {
        &:after {
          opacity: 0.24;
        }
      }
    }
  }
</style>
